import './EditableFormItem.style.less'
import { IInputProperty } from '@/screens/Order/interface/common.interface'
import {
  PlusOutlined,
  SettingOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import {
  Form,
  Input,
  DatePicker,
  Upload,
  Button,
  InputNumber,
  message,
  Select,
  Divider,
  Space,
  notification,
} from 'antd'
import { useEffect, useState } from 'react'
import { useAtom } from 'jotai'
import { masterDataAtom } from '@/jotai/master.jotai'
import useMasterData from '@/hook/MasterData'
import LoadingComponent from '@/components/LoadingComponent/LoadingComponent'
import { onAddNewPropertyValue } from '../../../Order.transform'
import { FIELD_INPUT_TYPE } from '../../../Order.constant'
import { getFieldName } from '../../EditOrder.helper'
import { useAuth } from '@/providers/auth.provider'
import { isEmptyValue } from '@/utils/common'
import { includes, uniqBy } from 'lodash'
import { UploadFile } from 'antd/es/upload'

interface Props {
  // index: number
  item: IInputProperty
  // onSave: any
  // onClose?: any
  // onDelete?: any
  onEdit: (value: any) => void
  disabled: boolean
  orderSetting: any
  hideProperties?: any
  autoPreGenerateField?: any
  form: any
  type: any
}

function EditableFormItem(props: Props) {
  const { item, hideProperties, autoPreGenerateField = {} } = props
  const [imageFileList, setImageFileList] = useState<UploadFile[]>([])
  const [masterData] = useAtom(masterDataAtom)
  const [newSelectInput, setNewSelectInput] = useState('')
  const [selectList, setSelectList] = useState([])
  const [loading, setloading] = useState(false)
  const [stateDisableInput, setStateDisableInput] = useState(props.disabled)
  const [statePreGenerate, setStatePreGenerate]: any = useState({})
  const { refetch } = useMasterData()

  const { user } = useAuth()

  const handleEditProperty = () => {
    props.onEdit(item)
  }

  const normFile = (e: any) => {
    if (Array.isArray(e)) {
      return e
    }
    return e?.fileList?.map((item) => {
      return { ...item, status: 'done' }
    })
  }

  useEffect(() => {
    if (props?.type === 'image') {
      if (
        !imageFileList.length &&
        Array.isArray(item?.value) &&
        item.value.length > 0
      ) {
        const files: UploadFile[] = item.value.map((file: any) => {
          const url = file.url || file.url_path || file.thumbnail_url_path // extra safety

          if (url) {
            return {
              uid: '-1',
              name: file.name,
              status: 'done',
              url,
            }
          } else {
            // Local file uploaded
            return file
          }
        })

        setImageFileList(files)
      }
    }

    if (includes([autoPreGenerateField?.field], item.field)) {
      setStatePreGenerate({
        formItemStyle: '',
        childStyle: 'pre-generate-layout',
      })
      setStateDisableInput(true)
    }
  }, [props.type, item.field, JSON.stringify(item.value)])

  const validateFileSize = (value) => {
    if (value.size > 5242880) {
      message.error('Each File cannot be over 5mbps', 3)
      return Upload.LIST_IGNORE
    }
  }

  const handleCheckAddNewSelect = (text: string = '') => {
    setNewSelectInput(text)
  }

  const handleAddNewSelectOption = async () => {
    setloading(true)
    const response = await onAddNewPropertyValue(item, newSelectInput, user)

    if (response?.data.statusCode == 400) {
      notification.error({
        message: response?.data.message,
        duration: 3,
        placement: 'top',
      })
    }
    await refetch()
    setloading(false)
  }

  const getDefaultSelectOptions = () => {
    const data = masterData.get(item.field)
    if (!isEmptyValue(data?.property_values)) {
      const options = []
      data?.property_values.forEach((element) => {
        options.push({ label: element.value, value: element.value })
      })
      setNewSelectInput('')
      setSelectList(uniqBy(options, 'value'))
    } else {
      setNewSelectInput('')
      setSelectList([])
    }
  }

  useEffect(() => {
    getDefaultSelectOptions()
  }, [item, masterData])

  const isHideItemBySettingFN = (field: string) =>
    includes(hideProperties, field)

  return (
    <>
      {loading && <LoadingComponent />}
      {props?.type == FIELD_INPUT_TYPE.TEXT ||
      props?.type == FIELD_INPUT_TYPE.STRING ? (
        <>
          <Form.Item
            hidden={isHideItemBySettingFN(item.field) || item.hidden}
            name={item.field}
            labelCol={{ span: 6 }}
            labelAlign="left"
            label={
              getFieldName(props?.orderSetting?.field_names, item.field) ??
              item.name
            }
            required={item.required}
            className={`editable-form-order ${statePreGenerate?.formItemStyle}`}
            rules={[
              {
                required: item.required,
                message: '',
              },
            ]}
            extra={
              !stateDisableInput && (
                <Button
                  onClick={handleEditProperty}
                  icon={<SettingOutlined />}
                />
              )
            }
          >
            <Input
              className={statePreGenerate?.childStyle}
              disabled={stateDisableInput}
              placeholder="Text"
              size="large"
            />
          </Form.Item>
        </>
      ) : props?.type == FIELD_INPUT_TYPE.NUMBER ||
        props?.type == FIELD_INPUT_TYPE.DECIMAL ? (
        <Form.Item
          hidden={isHideItemBySettingFN(item.field)}
          name={item.field}
          labelCol={{ span: 6 }}
          labelAlign="left"
          label={
            getFieldName(props?.orderSetting?.field_names, item.field) ??
            item.name
          }
          required={item.required}
          className="editable-form-order"
          rules={[
            {
              required: item.required,
              message: '',
            },
          ]}
          extra={
            !stateDisableInput && (
              <Button onClick={handleEditProperty} icon={<SettingOutlined />} />
            )
          }
        >
          <InputNumber
            disabled={stateDisableInput}
            placeholder="Number"
            size="large"
            onWheel={(e: any) => e.target.blur()}
            formatter={(value) =>
              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
            }
          />
        </Form.Item>
      ) : props?.type == FIELD_INPUT_TYPE.DATETIME || props?.type == 'date' ? (
        <Form.Item
          hidden={isHideItemBySettingFN(item.field)}
          name={item.field}
          labelCol={{ span: 6 }}
          labelAlign="left"
          className="editable-form-order"
          label={
            getFieldName(props?.orderSetting?.field_names, item.field) ??
            item.name
          }
          required={item.required}
          rules={[
            {
              required: item.required,
              message: '',
            },
          ]}
          extra={
            !stateDisableInput && (
              <Button onClick={handleEditProperty} icon={<SettingOutlined />} />
            )
          }
        >
          <DatePicker
            disabled={stateDisableInput}
            placeholder="Date"
            size="large"
            format={'DD MMM YYYY'}
          />
        </Form.Item>
      ) : props?.type == FIELD_INPUT_TYPE.FILE ? (
        <Form.Item
          hidden={isHideItemBySettingFN(item.field)}
          label={
            getFieldName(props?.orderSetting?.field_names, item.field) ??
            item.name
          }
          name={item.field}
          labelCol={{ span: 6 }}
          labelAlign="left"
          className="editable-form-order"
          valuePropName="fileList"
          getValueFromEvent={normFile}
          required={item.required}
          rules={[
            {
              required: item.required,
              message: 'This field is required',
            },
          ]}
          extra={
            !stateDisableInput && (
              <Button onClick={handleEditProperty} icon={<SettingOutlined />} />
            )
          }
        >
          <Upload
            disabled={stateDisableInput}
            maxCount={10}
            multiple
            accept=".doc,.docx,.docm,.xls,.xlsx,xlsm,.csv,.ppt,.pptx,.ppts,.txt,.rtf,.md,.jpg,.jpeg,.png,.bmp,.tif,.tiff,.svg,.eps,.raw,.psd,.ai,.pdf,.ico,.webp,.gif"
            beforeUpload={validateFileSize}
          >
            <Button
              className="upload-button"
              icon={<UploadOutlined />}
              size="large"
            >
              Select File
            </Button>
          </Upload>
        </Form.Item>
      ) : props?.type == 'image' ? (
        <Form.Item
          hidden={isHideItemBySettingFN(item.field)}
          label={
            getFieldName(props?.orderSetting?.field_names, item.field) ??
            item.name
          }
          name={item.field}
          labelCol={{ span: 6 }}
          labelAlign="left"
          className="editable-form-order"
          valuePropName="fileListImage"
          getValueFromEvent={(e: any) => {
            return Array.isArray(e) ? e : e?.fileList
          }}
          required={item.required}
          rules={[
            {
              required: item.required,
              message: '',
            },
          ]}
          extra={
            !stateDisableInput && (
              <Button onClick={handleEditProperty} icon={<SettingOutlined />} />
            )
          }
        >
          <Upload
            onChange={(info) => {
              setImageFileList(info.fileList)
            }}
            disabled={stateDisableInput}
            listType="picture-card"
            maxCount={1}
            accept="image/png, image/jpeg, image/jpg,image/gif"
            beforeUpload={() => false}
            fileList={imageFileList}
          >
            {imageFileList.length > 0 ? null : ' + Picture'}
          </Upload>
        </Form.Item>
      ) : props?.type == 'select' ? (
        <Form.Item
          hidden={isHideItemBySettingFN(item.field)}
          name={item.field}
          labelCol={{ span: 6 }}
          labelAlign="left"
          label={
            getFieldName(props?.orderSetting?.field_names, item.field) ??
            item.name
          }
          required={item.required}
          className="editable-form-order"
          rules={
            item.required && [
              {
                required: true,
                message: '',
              },
            ]
          }
          extra={
            !stateDisableInput && (
              <Button onClick={handleEditProperty} icon={<SettingOutlined />} />
            )
          }
        >
          <Select
            showSearch
            allowClear
            placeholder="Select"
            defaultValue={item.value}
            size="large"
            onSearch={handleCheckAddNewSelect}
            disabled={stateDisableInput}
            // onBlur={() => setNewSelectInput('')}
            dropdownRender={(menu) => (
              <div
                onMouseDown={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                }}
              >
                {menu}
                {item.dynamic !== false && (
                  <Space style={{ padding: '0 8px 4px', zIndex: 10 }}>
                    <Divider style={{ margin: '8px 0' }} />
                    <Button
                      disabled={newSelectInput === ''}
                      className="add-new-button"
                      icon={<PlusOutlined className="add-new-button" />}
                      type="text"
                      block
                      onClick={handleAddNewSelectOption}
                    >
                      Add new
                    </Button>
                  </Space>
                )}
              </div>
            )}
            options={selectList}
          />
        </Form.Item>
      ) : null}
    </>
  )
}

export default EditableFormItem
